import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Card,
  Col,
  Input,
  Modal,
  Row,
  Space,
  Switch,
  Typography,
  message,
} from 'antd';
import { AppstoreOutlined, PlusOutlined, ProjectOutlined } from '@ant-design/icons';
import AppBreadcrumb from '../../components/Breadcrumb';
import ParamListView from '../../components/ParamListView';
import { addCTModule, getCTModules, getCTSubmodules, setCTModuleStatus } from '../../services/testingApi';
import { getSelectedProject } from '../../services/authApi';


const { Text } = Typography;

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Test Cases', href: '/addTestCase' },
  { label: 'Manage Modules', href: '/manageModules' },
];

export default function ManageModules() {
  const navigate = useNavigate();
  const project = useMemo(() => getSelectedProject(), []);

  const [modules, setModules] = useState([]);
  const [counts, setCounts] = useState({});
  const [modulesLoading, setModulesLoading] = useState(false);
  const [addOpen, setAddOpen] = useState(false);
  const [moduleName, setModuleName] = useState('');
  const [saving, setSaving] = useState(false);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(10);

  const loadModules = useCallback(async (projectName) => {
    if (!projectName) {
      setModules([]);
      return;
    }
    setModulesLoading(true);
    try {
      const [modulesRes, submodulesRes] = await Promise.all([
        getCTModules({ project: projectName }),
        getCTSubmodules({ project: projectName }),
      ]);
      setModules(modulesRes?.items ?? []);

      // count submodules per module name
      const countMap = {};
      (submodulesRes?.items ?? []).forEach((sub) => {
        const key = sub.modulename;
        countMap[key] = (countMap[key] ?? 0) + 1;
      });
      setCounts(countMap);
    } catch (error) {
      console.error(error);
      message.error('Unable to load modules.');
    } finally {
      setModulesLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!project) {
      navigate('/select-project', { replace: true });
      return;
    }
    Promise.resolve().then(() => loadModules(project.name));
  }, [project, navigate, loadModules]);

  const handleToggle = async (record, checked) => {
    try {
      await setCTModuleStatus(record.id, checked ? 1 : 0);
      setModules((rows) =>
        rows.map((r) => (r.id === record.id ? { ...r, active: checked ? 1 : 0 } : r)),
      );
      message.success(`Module ${checked ? 'activated' : 'deactivated'}.`);
    } catch (error) {
      console.error(error);
      message.error('Unable to update module status.');
    }
  };

  const handleAdd = async () => {
    const name = moduleName.trim();
    if (!name) {
      message.warning('Enter a module name.');
      return;
    }
    setSaving(true);
    try {
      await addCTModule({ project: project.name, modulename: name });
      message.success('Module added.');
      setAddOpen(false);
      setModuleName('');
      setListPage(1);
      loadModules(project.name);
    } catch (error) {
      console.error(error);
      message.error('Unable to add module (maybe it already exists).');
    } finally {
      setSaving(false);
    }
  };

  const goToSubmodules = (record) => {
    navigate(`/test-cases/zinnext/submodules?module=${encodeURIComponent(record.modulename)}`);
  };

  const fields = [
    { label: 'Module', value: 'modulename' },
    {
      label: 'Submodules',
      value: 'submoduleCount',
      width: 140,
      render: (_value, record) => (
        <Button type="link" className="zt-count-link" onClick={() => goToSubmodules(record)}>
          {counts[record.modulename] ?? 0} submodule(s)
        </Button>
      ),
    },
    {
      label: 'Active',
      value: 'active',
      width: 120,
      render: (value, record) => (
        <Switch
          checked={value === 1}
          checkedChildren="On"
          unCheckedChildren="Off"
          onChange={(checked) => handleToggle(record, checked)}
        />
      ),
    },
  ];

  if (!project) return null;

  return (
    <main className="jobs-list-page">
      <Row align="middle" justify="space-between" className="breadcrumb-action-wrapper">
        <Col>
          <AppBreadcrumb items={breadcrumbItems} />
        </Col>
        <Col>
          <Button type="link" onClick={() => navigate('/addTestCase')}>
            Back to Form
          </Button>
        </Col>
      </Row>

      <div className="zt-page">
        <Card className="v1-card zt-bar">
          <Row gutter={[16, 16]} align="middle" justify="space-between">
            <Col>
              <Space size={6} className="zt-section-title">
                <ProjectOutlined />
                <span>Project:</span>
                <Text strong>{project.name}</Text>
              </Space>
            </Col>
            <Col>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddOpen(true)}
              >
                Add Module
              </Button>
            </Col>
          </Row>
        </Card>

        <Card
          className="v1-card zt-fall"
          title={(
            <Space size={6} className="zt-section-title">
              <AppstoreOutlined />
              <span>Modules</span>
            </Space>
          )}
        >
          <ParamListView
            listName="Modules"
            loading={modulesLoading}
            fields={fields}
            dataSource={modules}
            current={listPage}
            pageSize={listPageSize}
            total={modules.length}
            onPageChange={setListPage}
            onPageSizeChange={(size) => {
              setListPage(1);
              setListPageSize(size);
            }}
            rowSelection={false}
            className="zt-list-plain"
          />
        </Card>
      </div>

      <Modal
        title="Add Module"
        open={addOpen}
        confirmLoading={saving}
        okText="Add"
        onOk={handleAdd}
        onCancel={() => { setAddOpen(false); setModuleName(''); }}
        destroyOnHidden
      >
        <Space direction="vertical" className="zt-modal-stack">
          <Text type="secondary">Project: <b>{project.name}</b></Text>
          <Input
            placeholder="Enter module name"
            maxLength={100}
            value={moduleName}
            onChange={(e) => setModuleName(e.target.value)}
            onPressEnter={handleAdd}
          />
        </Space>
      </Modal>
    </main>
  );
}
